var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
const u = up.util;
const e = up.element;
up.FormValidator = class FormValidator {
    constructor(form) {
        this._form = form;
        this._dirtySolutions = [];
        this._nextRenderTimer = null;
        this._rendering = false;
        this._honorAbort();
    }
    start() {
        let guard = (field) => this._isValidatingField(field);
        let callback = (field) => this._onFieldAdded(field);
        return up.form.trackFields(this._form, { guard }, callback);
    }
    _isValidatingField(field) {
        return field.closest('[up-validate]:not([up-validate=false])');
    }
    _onFieldAdded(field) {
        let eventType = up.form.validateOptions(field).event;
        return up.on(field, eventType, (event) => {
            if (!field.closest('.up-keeping')) {
                up.log.putsEvent(event);
                up.error.muteUncriticalRejection(this.validate({ origin: field }));
            }
        });
    }
    _honorAbort() {
        up.fragment.onAborted(this._form, (event) => this._onAborted(event));
    }
    _onAborted(event) {
        let abortedError = new up.Aborted(event.reason);
        let solution;
        while (solution = this._dirtySolutions.shift()) {
            solution.deferred.reject(abortedError);
        }
    }
    validate(options = {}) {
        var _a;
        let newSolutions = this._getSolutions(options);
        this._dirtySolutions.push(...newSolutions);
        this._scheduleNextRender();
        return (_a = newSolutions[0]) === null || _a === void 0 ? void 0 : _a.deferred;
    }
    _getSolutions(options) {
        let solutions = this._getTargetSelectorSolutions(options)
            || this._getFieldSolutions(options)
            || this._getElementSolutions(options.origin);
        let deferred = u.newDeferred();
        for (let solution of solutions) {
            let renderOptions = up.form.validateOptions(solution.origin, options);
            solution.batch = u.pluckKey(renderOptions, 'batch');
            solution.renderOptions = renderOptions;
            solution.destination = `${renderOptions.method} ${renderOptions.url}`;
            solution.target = up.fragment.resolveOrigin(solution.target, solution);
            solution.deferred = deferred;
        }
        return solutions;
    }
    _getFieldSolutions(_a) {
        var { origin } = _a, options = __rest(_a, ["origin"]);
        if (up.form.isField(origin)) {
            return this._getValidateAttrSolutions(origin) || this._getFormGroupSolutions(origin, options);
        }
    }
    _getFormGroupSolutions(field, { formGroup = true }) {
        if (!formGroup)
            return;
        let solution = up.form.groupSolution(field);
        if (solution) {
            up.puts('up.validate()', 'Validating form group of field %o', field);
            return [solution];
        }
    }
    _getTargetSelectorSolutions({ target, origin }) {
        if (u.isString(target)) {
            up.puts('up.validate()', 'Validating target "%s"', target);
            let simpleSelectors = up.fragment.splitTarget(target);
            return u.compact(simpleSelectors.map(function (simpleSelector) {
                let element = up.fragment.get(simpleSelector, { origin });
                if (element) {
                    return {
                        element,
                        target: simpleSelector,
                        origin
                    };
                }
                else {
                    up.fail('Validation target "%s" does not match an element', simpleSelector);
                }
            }));
        }
    }
    _getElementSolutions(element) {
        up.puts('up.validate()', 'Validating element %o', element);
        return [{
                element,
                target: up.fragment.toTarget(element),
                origin: element
            }];
    }
    _getValidateAttrSolutions(field) {
        let containerWithAttr = field.closest('[up-validate]');
        if (containerWithAttr) {
            let target = e.booleanOrStringAttr(containerWithAttr, 'up-validate');
            return this._getTargetSelectorSolutions({ target, origin: field });
        }
    }
    _scheduleNextRender() {
        let solutionDelays = this._dirtySolutions.map((solution) => solution.renderOptions.delay);
        let shortestDelay = Math.min(...solutionDelays) || 0;
        clearTimeout(this._nextRenderTimer);
        this._nextRenderTimer = u.timer(shortestDelay, () => {
            this._nextRenderTimer = null;
            this._renderDirtySolutions();
        });
    }
    _renderDirtySolutions() {
        up.error.muteUncriticalRejection(this._doRenderDirtySolutions());
    }
    _doRenderDirtySolutions() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._dirtySolutions.length)
                return;
            if (this._rendering)
                return;
            if (this._nextRenderTimer)
                return;
            let solutionsBatch = this._selectDirtySolutionsBatch();
            let renderOptions = this._mergeRenderOptions(solutionsBatch);
            this._rendering = true;
            try {
                let renderPromise = up.render(renderOptions);
                for (let solution of solutionsBatch) {
                    solution.deferred.resolve(renderPromise);
                }
                yield renderPromise;
            }
            finally {
                this._rendering = false;
                this._renderDirtySolutions();
            }
        });
    }
    _selectDirtySolutionsBatch() {
        let batch = [];
        let i = 0;
        while (i < this._dirtySolutions.length) {
            let solution = this._dirtySolutions[i];
            if (batch.length === 0 || this._canBatchSolutions(batch[0], solution)) {
                batch.push(solution);
                this._dirtySolutions.splice(i, 1);
            }
            else {
                i++;
            }
        }
        return batch;
    }
    _canBatchSolutions(s1, s2) {
        return s1.destination === s2.destination && s1.batch && s2.batch;
    }
    _mergeRenderOptions(dirtySolutions) {
        var _a;
        let dirtyOrigins = u.map(dirtySolutions, 'origin');
        let dirtyFields = u.flatMap(dirtyOrigins, up.form.fields);
        let dirtyNames = u.uniq(u.map(dirtyFields, 'name'));
        let dirtyRenderOptionsList = u.map(dirtySolutions, 'renderOptions');
        let formDestinationOptions = up.form.destinationOptions(this._form);
        let options = u.mergeDefined(formDestinationOptions, ...dirtyRenderOptionsList);
        options.target = u.map(dirtySolutions, 'target').join(', ');
        options.origin = this._form;
        (_a = options.focus) !== null && _a !== void 0 ? _a : (options.focus = 'keep');
        options.failOptions = false;
        options.defaultMaybe = true;
        options.params = up.Params.merge(formDestinationOptions.params, ...u.map(dirtyRenderOptionsList, 'params'));
        options.headers = u.merge(formDestinationOptions.headers, ...u.map(dirtyRenderOptionsList, 'headers'));
        this._addValidateHeader(options.headers, dirtyNames);
        options.feedback = u.some(dirtyRenderOptionsList, 'feedback');
        options.data = undefined;
        options.dataMap = u.mapObject(dirtySolutions, ({ target, element, renderOptions: { data, keepData } }) => [
            target,
            keepData ? up.data(element) : data
        ]);
        options.preview = undefined;
        options.previewMap = u.mapObject(dirtySolutions, ({ target, renderOptions: { preview } }) => [target, preview]);
        options.placeholder = undefined;
        options.placeholderMap = u.mapObject(dirtySolutions, ({ target, renderOptions: { placeholder } }) => [target, placeholder]);
        options.disable = dirtySolutions.map((solution) => up.fragment.resolveOrigin(solution.renderOptions.disable, solution));
        options.guardEvent = up.event.build('up:form:validate', {
            fields: dirtyFields,
            log: 'Validating form',
            params: options.params,
            form: this._form,
        });
        return options;
    }
    _addValidateHeader(headers, names) {
        let key = up.protocol.headerize('validate');
        let value = names.join(' ');
        if (!value || value.length > up.protocol.config.maxHeaderSize)
            value = ':unknown';
        headers[key] = value;
    }
};
